import React, { useMemo } from 'react';
import { View, Text, StyleSheet, Pressable, ImageBackground, ScrollView, Dimensions, Image } from 'react-native';
import { Spacing, Mixins, Typography, Colors } from '@styles';
import { useTheme } from '@themeProvider';
import { useLanguage } from '@languageProvider';
import { Button, GeneralTag, BaseAvatar } from '@atoms';
import { parsePrice } from '@utils/helpers';
import { SmartFlatGridView, SmartWrapView } from '@lib';
import { Promotion, ProductNode, SelectionTree, Coupon } from '@datamodel';

export const ProductNodeCard = ({ product, offers={}, couponAvailable, style, numberInCart=0, ...props }) => {
    const { useColor, useElevation } = useTheme();
    const { translate } = useLanguage();
    const [title, label, primaryText, body, backgroundColor, secondary, primary] = useColor([
        'title',
        'label',
        'primary',
        'body',
        'background',
        'secondary',
        ['primary', { isText: true }]]);
    const [numberColor] = useColor([
        ['title', { on: primary }]
    ])

    const soldOut = ProductNode.isSoldOut(product);

    const { promotionId: appliedOffer, price: finalPrice } = Promotion.calculateBestOffer(offers, product.uiPrice);

    return (
        <Pressable {...props} disabled={soldOut} style={[style, Mixins.HORIZONTAL]}>

            <ImageBackground
                style={{
                    width: ProductNodeCard.HEIGHT,
                    height: ProductNodeCard.HEIGHT,
                    ...Mixins.rounding(ProductNodeCard.HEIGHT),
                    // backgroundColor: inputBackground,
                    overflow: 'hidden'
                }}
                source={{ uri: product.photo }}
                resizeMode='contain'
            />

            {
                !!soldOut &&
                <View style={{
                    position: 'absolute',
                    width: ProductNodeCard.HEIGHT,
                    height: ProductNodeCard.HEIGHT,
                    backgroundColor: Colors.setOpacity(label, .5),
                    ...Mixins.rounding(ProductNodeCard.HEIGHT)
                }} />
            }

            <View style={{ marginLeft: Spacing.M, flex: 1, marginRight: Spacing.XL, marginTop: Spacing.XXS }}>
                <Text style={[Typography.TITLE, { color: soldOut ? label : title }]}>{product.name}</Text>
                <Text style={{ textAlignVertical: 'bottom' }}>
                    {
                        !!product.fromFlag &&
                        <Text style={[Typography.SMALL_COPY, { color: label }]}>{translate('from')} </Text>
                    }
                    <Text style={[Typography.TITLE, { color: soldOut ? label : primaryText }]}>{parsePrice(finalPrice, '€')}  </Text>
                    {!!appliedOffer &&
                        <Text style={[Typography.TITLE, { color: label, textDecorationLine: 'line-through' }]}>{parsePrice(product.uiPrice, '€')}</Text>
                    }
                </Text>
                {
                    !!product.description &&
                    <Text style={[Typography.BODY, { color: body }]}>{product.description}</Text>
                }
                <View style={[Mixins.HORIZONTAL, { flexWrap: 'wrap' }]}>
                    {
                        !!soldOut &&
                        <GeneralTag
                            dense
                            style={{ alignSelf: 'flex-start', marginTop: Spacing.XXS, marginRight: Spacing.XXS }}
                            displayText={translate('soldOut')}
                            type="transparent"
                            color={title} />
                    }
                    {
                        !soldOut && !!couponAvailable &&
                        <GeneralTag
                            icon="discount"
                            dense
                            style={{ alignSelf: 'flex-start', marginTop: Spacing.XXS, marginRight: Spacing.XXS }}
                            displayText={translate('couponAvailable')}
                            type="transparent"
                            color={secondary} />
                    }
                    {
                        !soldOut && product.popular &&
                        <GeneralTag
                            dense
                            style={{ alignSelf: 'flex-start', marginTop: Spacing.XXS, marginRight: Spacing.XXS }}
                            displayText="Popular"
                            type="transparent"
                            color={primary} />
                    }
                </View>

            </View>

            {numberInCart >= 1 &&
                <View
                    style={{
                        borderWidth: 2,
                        borderColor: backgroundColor,
                        width: Typography.SMALL_COPY.lineHeight * 1.3,
                        height: Typography.SMALL_COPY.lineHeight * 1.3,
                        ...Mixins.rounding(Typography.SMALL_COPY.lineHeight * 1.3),
                        backgroundColor: primary,
                        justifyContent: 'center',
                        position: 'absolute',
                        left: ProductNodeCard.HEIGHT - 3 / 4 * 1.3 * Typography.SMALL_COPY.lineHeight,
                        top: ProductNodeCard.HEIGHT - 3 / 4 * 1.3 * Typography.SMALL_COPY.lineHeight,
                        // top: - Typography.SMALL_COPY.lineHeight * 1.3 / 4,
                    }}
                >
                    <Text style={[Typography.STRONG, { textAlign: 'center', color: numberColor, fontSize: Typography.SMALL_COPY.fontSize }]}>{numberInCart}</Text>
                </View>
            }

        </Pressable>
    )


}
ProductNodeCard.HEIGHT = 88;


export const ProductItemCard = ({ product, offers = {}, couponAvailable, style, numberInCart = 0, ...props }) => {
    const { useColor, useElevation } = useTheme();
    const elevation = useElevation(1);
    const { translate } = useLanguage();
    const [title, label, primaryText, body, backgroundColor, secondary, primary, line] = useColor([
        'title',
        'label',
        'primary',
        'body',
        'background',
        'secondary',
        ['primary', { isText: true }],
        'line']);
    const [numberColor] = useColor([
        ['title', { on: primary }]
    ])

    const soldOut = ProductNode.isSoldOut(product);

    const { promotionId: appliedOffer, price: finalPrice } = Promotion.calculateBestOffer(offers, product.uiPrice);

    const borderRadius = Mixins.rounding(ProductItemCard.HEIGHT).borderRadius
    return (
        <Pressable {...props}
            disabled={soldOut}
            style={[
                style,
                Mixins.HORIZONTAL,
                !soldOut ? elevation : {}, {
                    borderWidth: !soldOut ? 1 : 0,
                    borderColor: Colors.setOpacity(line, .25),
                    borderRadius,
                    overflow: 'hidden',
        }]}>

            <ImageBackground
                style={{
                    width: ProductItemCard.HEIGHT,
                    height: ProductItemCard.HEIGHT,
                    ...Mixins.radius([ borderRadius, 0, 0, borderRadius]),
                    overflow: 'hidden',
                    borderRightWidth: 1,
                    borderColor: line,
                }}
                source={{ uri: product.photo }}
                resizeMode='contain'
            />

            {
                !!soldOut &&
                <View style={{
                    position: 'absolute',
                    top: 0, left: 0, right: 0, bottom: 0,
                    backgroundColor: Colors.setOpacity(label, .5),
                    ...Mixins.radius([borderRadius, 0, 0, borderRadius]),
                }} />
            }

            <View style={{ marginHorizontal: Spacing.M, flex: 1, justifyContent: 'center' }}>
                <Text numberOfLines={product.name.split(' ').length > 1 ? 2 : 1} style={[Typography.SMALL_TITLE, { color: soldOut ? label : title }]}>{product.name}</Text>
            </View>

            {numberInCart >= 1 &&
                <View
                    style={{
                        borderWidth: 2,
                        borderColor: backgroundColor,
                        width: Typography.SMALL_COPY.lineHeight * 1.3,
                        height: Typography.SMALL_COPY.lineHeight * 1.3,
                        ...Mixins.rounding(Typography.SMALL_COPY.lineHeight * 1.3),
                        backgroundColor: primary,
                        justifyContent: 'center',
                        position: 'absolute',
                        left: ProductItemCard.HEIGHT - 3 / 4 * 1.3 * Typography.SMALL_COPY.lineHeight,
                        top: ProductItemCard.HEIGHT - 3 / 4 * 1.3 * Typography.SMALL_COPY.lineHeight,
                        // top: - Typography.SMALL_COPY.lineHeight * 1.3 / 4,
                    }}
                >
                    <Text style={[Typography.STRONG, { textAlign: 'center', color: numberColor, fontSize: Typography.SMALL_COPY.fontSize }]}>{numberInCart}</Text>
                </View>
            }

        </Pressable>
    )


}
ProductItemCard.HEIGHT = 44;

export const FungibleCard = ({ style, product, options, offers={}, coupons={}, cartCount={}, displayModal, ...props}) => {
    let PCard = props.isPackItem ? ProductItemCard : ProductNodeCard;
    return (<>
        { !options || options.length === 0 ?
            <PCard
                offers={offers}
                couponAvailable={Object.keys(coupons).length > 0}
                numberInCart={cartCount[product.nodeId]}
                onPress={() => displayModal(product)}
                product={product}
                style={style}
                {...props}/>
            :
            <>
            { options.map( option  => (
                <PCard
                    offers={offers}
                    couponAvailable={Object.keys(coupons).length > 0}
                    numberInCart={cartCount[option.nodeId]}
                    onPress={() => displayModal(option)}
                    style={style}
                    product={option}
                    key={option.nodeId} />
            ))}
            </>
        }
    </>)
}

const PackItem = ({ packItem, style, photoStyle, ...props }) => {
    const { useColor } = useTheme();
    const [body, primaryText, primary] = useColor(['body', ['primary', { isText: true }], 'primary']);

    const contrastedPrimary = Colors.withMinimumWhiteContrast(primary, 18);

    return <View style={[Mixins.HORIZONTAL, { alignItems: 'center' }, style]} {...props}>
        <ImageBackground source={{ uri: packItem.product.photo }} style={[photoStyle, { height: 40, width: 40, overflow: 'hidden', alignItems: 'center', justifyContent: 'center' }]}>
            <View style={[StyleSheet.absoluteFill, { backgroundColor: Colors.setOpacity(contrastedPrimary, .35) }]} />
            <Text style={[Typography.TITLE, { color: 'white', textAlign: 'center', zIndex: 1 }]}>{packItem.quantity}</Text>
        </ImageBackground>
        <Text style={[Typography.SMALL_COPY, { color: body, marginLeft: Spacing.XS }]}>{packItem.product.name}</Text>
    </View>
}

export const ProductPackCard = ({ pack, style, cartCount, ...props }) => {
    const { useColor, useElevation } = useTheme();
    const elevation = useElevation(4);
    const rounding = Mixins.rounding(ProductNodeCard.HEIGHT);
    const [ title, body, primaryText, line, label, backgroundColor ] = useColor([
        'title',
        'body',
        ['primary', { isText: true }],
        'line',
        'label',
        'background',
    ])

    const [numberColor] = useColor([
        ['title', { on: primaryText }]
    ]);

    const packItems = Object.keys(pack.items).map(id => pack.items[id]);

    const [price, originalPrice] = useMemo(() => {
        return Object.keys(pack.items).reduce((acc, itemId) => {
            const item = pack.items[itemId];
            const fromPrice = ProductNode.calculateTreeFromPrices(item.product);

            // TODO: Check this
            let unitPrice = item.product.maxChildren === 0 ? item.product.price.amount : fromPrice[item.product.nodeId] + item.product.price.amount;
            let itemPrice = unitPrice * item.quantity;
            let ogItemPrice = itemPrice;
            if (item.coupon) {
                itemPrice = Coupon.discountedPrice(item.coupon, ogItemPrice);
            }
            return [acc[0] + itemPrice, acc[1] + ogItemPrice];
        }, [0,0]);
    });

    return <Pressable style={[elevation, rounding, style]} {...props}>
        <View style={[Mixins.HORIZONTAL, { borderBottomWidth: 1, borderColor: line }]}>
            <Image style={{width: ProductNodeCard.HEIGHT, borderTopLeftRadius: rounding.borderRadius }} source={{ uri: pack.photo }}/>
            <View style={{ marginLeft: Spacing.M, paddingVertical: Spacing.XS, flex: 1 }}>
                <Text style={[Typography.TITLE, { color: title }]}>{pack.name}</Text>
                <Text style={{ textAlignVertical: 'bottom' }}>
                    <Text style={[Typography.TITLE, { color: false && soldOut ? label : primaryText }]}>{parsePrice(price, '€')}  </Text>
                    { price < originalPrice  &&
                        <Text style={[Typography.TITLE, { color: label, textDecorationLine: 'line-through' }]}>{parsePrice(originalPrice, '€')}</Text>
                    }
                </Text>

            </View>
        </View>

        <Text style={[Typography.SUBTITLE, { color: body, marginHorizontal: Spacing.S, marginTop: Spacing.XS }]}>{pack.description}</Text>
        <SmartWrapView flexDirection='row' crossDirectionSize={40 + Spacing.XS} style={{ marginBottom: Spacing.S }}>
            {packItems.map(packItem => <PackItem key={packItem.itemId} style={{ marginLeft: Spacing.S, marginTop: Spacing.XS }} packItem={packItem} photoStyle={Mixins.matchRounding(rounding, -Spacing.S)} />)}
        </SmartWrapView>

        {cartCount >= 1 &&
            <View
                style={{
                    borderWidth: 2,
                    borderColor: backgroundColor,
                    width: Typography.SMALL_COPY.lineHeight * 1.3,
                    height: Typography.SMALL_COPY.lineHeight * 1.3,
                    ...Mixins.rounding(Typography.SMALL_COPY.lineHeight * 1.3),
                    backgroundColor: primaryText,
                    justifyContent: 'center',
                    position: 'absolute',
                    bottom: -1 / 4 * 1.3 * Typography.SMALL_COPY.lineHeight,
                    right: -1 / 4 * 1.3 * Typography.SMALL_COPY.lineHeight,
                }}
            >
                <Text style={[Typography.STRONG, { textAlign: 'center', color: numberColor, fontSize: Typography.SMALL_COPY.fontSize }]}>{cartCount}</Text>
            </View>
        }

    </Pressable>
}

export const SelectedProductInfo = ({product, selectionTree, style, ...props}) => {
    const { useColor } = useTheme();
    const [ body, title, primary ] = useColor(['body', 'title', ['primary', { isText: true }]]);
    const mainNode = ProductNode.getTreeNodes(product, [selectionTree._mainSelection])[selectionTree._mainSelection];
    const leafs = SelectionTree.getMarkedLeafs(selectionTree).map(l => l.name).filter(n => n !== mainNode.name);

    return <Pressable style={[Mixins.HORIZONTAL, { alignItems: leafs.length > 0 ? 'flex-start' : 'center', paddingHorizontal: Spacing.M }, style]} {...props}>
        <Image style={[Mixins.rounding(44), { height: 44, width: 44 }]} source={{ uri: mainNode?.photo }}/>
        <View style={{ marginHorizontal: Spacing.XS, flex: 1  }}>
            <View style={[Mixins.HORIZONTAL, { justifyContent: 'space-between', alignItems: 'center'}]}>
                <Text style={[Typography.SUBTITLE, { color: title, flex: 1, overflow: 'hidden'}]}>{mainNode.name}</Text>
                {/* <Text style={[Typography.SUBTITLE, { color: primary }]}>{parsePrice(SelectionTree.calculatePrice(selectionTree), '€')}</Text> */}
            </View>
            { leafs.length > 0 &&
                <Text style={[Typography.BODY, { color: body }]}>{leafs.join(' · ')}</Text>
            }
        </View>
    </Pressable>
}